/* global AbortController, HTMLElement, document, window */

const fallbackBodyLocks = new Set()
let fallbackBodyOverflow = ''

function lockBodyOverflow() {
  if (typeof window.themeMigrationLockBodyOverflow === 'function')
    return window.themeMigrationLockBodyOverflow()
  if (!globalThis.document?.body) return () => {}

  const token = Symbol('3d-maker-body-overflow')
  if (fallbackBodyLocks.size === 0) fallbackBodyOverflow = document.body.style.overflow
  fallbackBodyLocks.add(token)
  document.body.style.overflow = 'hidden'
  let active = true
  return () => {
    if (!active) return
    active = false
    fallbackBodyLocks.delete(token)
    if (fallbackBodyLocks.size === 0) document.body.style.overflow = fallbackBodyOverflow
  }
}

export class ThreeDMakerStories extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback()
    this.abortController = new AbortController()
    this.dialog = this.querySelector('[data-3d-dialog]')
    this.carousel = this.querySelector('[data-maker-carousel]')
    this.progress = this.querySelector('[data-maker-progress]')
    this.triggers = [...this.querySelectorAll('[data-maker-open]')]
    this.videos = [...this.querySelectorAll('[data-maker-video]')]
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.playToken = 0

    for (const trigger of this.triggers)
      trigger.addEventListener('click', this.handleOpen, { signal: this.abortController.signal })
    for (const close of this.querySelectorAll('[data-maker-close]'))
      close.addEventListener('click', this.handleClose, { signal: this.abortController.signal })
    for (const toggle of this.querySelectorAll('[data-maker-toggle]'))
      toggle.addEventListener('click', this.handleToggle, { signal: this.abortController.signal })
    for (const video of this.videos) {
      video.addEventListener('play', this.handlePlaybackState, {
        signal: this.abortController.signal,
      })
      video.addEventListener('pause', this.handlePlaybackState, {
        signal: this.abortController.signal,
      })
    }
    this.dialog?.addEventListener('click', this.handleBackdrop, {
      signal: this.abortController.signal,
    })
    this.addEventListener('keydown', this.handleKeydown, { signal: this.abortController.signal })
    this.addEventListener('shopify:block:select', this.handleBlockSelect, {
      signal: this.abortController.signal,
    })
    for (const eventName of ['control:prev', 'control:next']) {
      this.addEventListener(eventName, this.handleCarouselControl, {
        capture: true,
        signal: this.abortController.signal,
      })
    }
    this.carousel?.addEventListener('scroll', this.updateProgress, {
      signal: this.abortController.signal,
      passive: true,
    })
    this.updateProgress()
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.abortController = null
    this.close(false)
    this.dialog = null
    this.carousel = null
    this.progress = null
    this.triggers = []
    this.videos = []
    this.reducedMotion = null
  }

  handleOpen = (event) => this.open(event.currentTarget.dataset.makerBlockId, event.currentTarget)
  handleClose = () => this.close(true)

  handleToggle = () => {
    const video = this.activeVideo
    if (!video) return
    if (video.paused) this.play(video)
    else video.pause?.()
  }

  handlePlaybackState = (event) => this.syncPlaybackState(event.currentTarget)

  syncPlaybackState(video) {
    if (this.activeVideo !== video) return
    this.dialog?.classList.toggle('paused', Boolean(video.paused))
  }

  handleBackdrop = (event) => {
    if (event.target === this.dialog) this.close(true)
  }

  handleKeydown = (event) => {
    if (!this.dialog || this.dialog.hidden) return
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close(true)
      return
    }
    if (event.key !== 'Tab') return
    const focusable = [
      ...this.dialog.querySelectorAll('button:not([disabled]), video[controls]'),
    ].filter((candidate) => {
      if (candidate.matches('video[controls]')) return candidate === this.activeVideo
      return candidate.getClientRects().length > 0
    })
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable.at(-1)
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus?.()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus?.()
    }
  }

  handleBlockSelect = (event) => {
    const trigger = this.triggers.find(
      (candidate) => candidate.dataset.makerBlockId === event.detail?.blockId,
    )
    if (trigger && !trigger.disabled) this.open(trigger.dataset.makerBlockId, trigger)
  }

  handleCarouselControl = (event) => {
    if (!this.reducedMotion?.matches) return
    const carousel = event.target?.closest?.('[data-maker-carousel]')
    if (!carousel || !this.contains(carousel)) return
    event.preventDefault()
    event.stopImmediatePropagation()
    if (event.type === 'control:prev') carousel.previous?.(false)
    else carousel.next?.(false)
  }

  open(blockId, opener) {
    if (!this.dialog) return
    const video = this.videos.find((candidate) => candidate.dataset.makerBlockId === blockId)
    if (!video) return
    this.close(false)
    this.opener = opener
    this.activeVideo = video
    this.dialog.hidden = false
    this.dialog.classList.add('active')
    this.releaseBodyOverflow = lockBodyOverflow()
    video.classList.add('active')
    this.dialog.querySelector('[data-maker-close]')?.focus?.()
    this.play(video)
  }

  play(video) {
    const token = ++this.playToken
    const result = video.play?.()
    if (!result?.then) {
      this.syncPlaybackState(video)
      return
    }
    result.then(
      () => {
        if (token === this.playToken && this.isConnected && this.activeVideo === video) {
          this.syncPlaybackState(video)
          return
        }
        video.pause?.()
      },
      () => {
        if (token === this.playToken && this.isConnected && this.activeVideo === video)
          this.dialog?.classList.add('paused')
      },
    )
  }

  close(restoreFocus) {
    this.playToken += 1
    for (const video of this.videos || []) {
      video.pause?.()
      try {
        video.currentTime = 0
      } catch {
        // A detached or non-seekable media element can reject reset.
      }
      video.classList?.remove('active')
    }
    if (this.dialog) {
      this.dialog.classList.remove('active', 'paused')
      this.dialog.hidden = true
    }
    this.releaseBodyOverflow?.()
    this.releaseBodyOverflow = null
    const opener = this.opener
    this.activeVideo = null
    this.opener = null
    if (restoreFocus) opener?.focus?.()
  }

  updateProgress = () => {
    if (!this.carousel || !this.progress) return
    const maximum = Math.max(1, this.carousel.scrollWidth - this.carousel.clientWidth)
    const ratio = Math.min(1, Math.max(0, this.carousel.scrollLeft / maximum))
    const track = this.progress.parentElement
    const travel = Math.max(0, (track?.clientWidth ?? 0) - this.progress.offsetWidth)
    this.progress.style.transform = `translate(${travel * ratio}px, -50%)`
  }
}

if (globalThis.customElements && !globalThis.customElements.get('lp-three-d-makers')) {
  globalThis.customElements.define('lp-three-d-makers', ThreeDMakerStories)
}
